/* eslint-disable camelcase */
import {
  useState,
  useEffect,
} from 'react';
import { format } from 'date-fns';
import { useTeamsData } from '@lib/hooks/useTeamsData';

import { useRouter } from 'next/router';

import { useStreamData } from '@lib/hooks/useStreamData';
import {
  postIpaUserEvent,
  wakeUpLambdaFunctions,
  connectToYoutubeStream,
} from '@services/youtube-platform.service';
import InteractionLayout from '@components/Sandbox/SandboxPreviewInteractionLayout';
import Loader from '@atoms/Atom/Loader';

interface Props {
  platformId: string;
  template: any;
  aiResponsesEnabled: boolean;
  idx?: number;
}

export default function RosPreviewAlpha({
  platformId = '',
  template = null,
  aiResponsesEnabled = true,
  idx = 0
}: Props) {
  const router = useRouter();
  const [linkQuestions, setLinkQuestions] = useState<any>([]);

  const [currentRosFeature, setCurrentRosFeature] = useState<any>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(Number(idx) || 0);

  const [crudLoading, setCrudLoading] = useState(true);
  const teamContext = useTeamsData((s) => s.teamContext);
  const { setIntervalId } = useStreamData(s => ({ setIntervalId: s.setIntervalId }));
  const [stream, setStream] = useState<any>(null);

  const connectToStream = async (event: any) => {
    await wakeUpLambdaFunctions();
    const newIntervalId = setInterval(async () => {
      await wakeUpLambdaFunctions();
    }, 15 * 60 * 1000);
    setIntervalId(newIntervalId as unknown as number);
    const scheduledStartTime = event.event_time;
    const engageType = new Date().getTime() > new Date(scheduledStartTime).getTime()
      ? 'active'
      : 'scheduled';

    const res = await connectToYoutubeStream({
      liveChatId: event.id,
      platformId: event.platform_id,
      statusFlag: true,
      engageType,
      platformType: event.livestream.platform_type,
      entityId: event.id,
      title: event.title,
      userId: event.livestream.user_id,
      orgStartTime: event.event_time,
      totalTimeWithText: '12 PM',
      template_id: template?.id || ''
    });

    if (!res.status) {
      console.log('Failed to create stream');
      return;
    }

    setStream(res.entity);

    if (!template) return;
    const excludedTypes = ['Select Interaction feature'];

    const tempQuestions = template.questions.filter(
      (q: Record<string, any>) => !excludedTypes.includes(q.type)
    );

    setLinkQuestions(tempQuestions);

    const safeIndex = Math.min(Math.max(currentIndex, 0), tempQuestions.length - 1);
    if (safeIndex !== currentIndex) setCurrentIndex(safeIndex);
    setCurrentRosFeature(tempQuestions[safeIndex]);

    localStorage.setItem('streamId', res.entity.id);
    localStorage.setItem('sessionId', res.entity.sessionId);
    window.parent.postMessage('ready', '*');
  };

  const connectSandbox = async () => {
    setCrudLoading(true);
    const date = new Date();
    const event_time = date.toISOString();

    const res = await postIpaUserEvent({
      platform_id: platformId,
      title: template?.title || '',
      event_time,
      user_id: teamContext?.oid,
      template_id: template?.id || '',
      event_type: 'ipa',
      is_sandbox: true,
      is_ros_preview: true,
      is_ai_generated: false,
      totalTimeWithText: format(new Date(event_time), 'hh:mm a')
    });

    connectToStream(res.entity);

    setCrudLoading(false);
  };

  useEffect(() => {
    if (!teamContext?.oid) return;

    connectSandbox();
  }, [teamContext]);

  useEffect(() => {
    router.push({
      pathname: router.pathname,
      query: { ...router.query, idx: currentIndex }
    }, undefined, { shallow: true });
  }, [currentIndex]);

  if (crudLoading || !stream || !linkQuestions?.length) return <Loader />;

  return (
    <div className='relative h-full bg-black'>
      <InteractionLayout
        isFullScreenMode
        defaultThemeSettings={{}}
        interactionType={{
          isResult: false,
          isRos: true
        }}
        onClose={window.close}
        streamId={stream?.id}
        colorData={currentRosFeature?.setting}
        data={currentRosFeature?.options}
        platformType={stream?.platform_type}
        totalQuestions={linkQuestions?.length}
        streamSetting={{
          ...(stream?.setting || {}),
          showOverlay: false
        }}
        streamName={stream?.title}
        rosIndex={currentIndex}
        questionIds={linkQuestions?.map((q: any) => q.id)}
        tempQuestions={linkQuestions}
        linkTemp={{
          layoutMode: template?.layout_mode || 'rectangle'
        }}
        template={template}
        aiResponsesEnabled={aiResponsesEnabled}
        onIndexChange={setCurrentIndex}
      />
    </div>
  );
}
